import "./StackedBarChart.css";

import type { Scale, TooltipItem } from "chart.js";
import { ChartData } from "chart.js";
import { Bar } from "react-chartjs-2";

import { maybeSetGlobalChartJsConfiguration } from "../utils";

maybeSetGlobalChartJsConfiguration();

export interface StackedChartData {
  label: string;
  data: number[];
  backgroundColor: string;
  hoverBackgroundColor: string;
}

export interface StackedBarChartProps {
  xLabels: (string | string[])[];
  data: StackedChartData[];
  emptyPlaceholder?: string;
  hasYAxisGridLines?: boolean;
  hasYAxisTicks?: boolean;
  showLegend?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  yAxisTickCallbackFunction?: (this: Scale, val: string | number, index: number) => any;
  customTooltipLabel?: (context: TooltipItem<"bar">) => string;
}

export const StackedBarChart = ({
  xLabels,
  data,
  emptyPlaceholder = "No data to display",
  hasYAxisTicks = false,
  hasYAxisGridLines = false,
  showLegend = true,
  yAxisTickCallbackFunction,
  customTooltipLabel,
}: StackedBarChartProps): JSX.Element => {
  const options = {
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: showLegend,
        reverse: true,
        position: "bottom" as const,
        labels: {
          padding: 40,
          boxWidth: 16,
          boxHeight: 16,
        },
      },
      tooltip: {
        displayColors: false,
        callbacks: {
          title: () => "",
          label:
            customTooltipLabel || ((context: TooltipItem<"bar">) => `${context.dataset.label}: ${context.parsed.y}`),
        },
      },
    },
    animation: {
      duration: 0,
    },
    devicePixelRatio: 4, // Use 4 for clarity
    responsive: true,
    scales: {
      x: {
        beginAtZero: true,
        stacked: true,
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        stacked: true,
        ticks: {
          callback: yAxisTickCallbackFunction,
          display: hasYAxisTicks,
          padding: 24,
        },
        grid: {
          display: hasYAxisGridLines,
          drawBorder: false,
          drawTicks: false,
        },
        border: {
          display: false,
        },
      },
    },
  };

  if (!data || Object.keys(data).length === 0)
    return (
      <div className="StackedBar StackedBarEmpty">
        <p className="body3">{emptyPlaceholder}</p>
      </div>
    );

  const chartData: ChartData<"bar", number[], string | string[]> = {
    labels: xLabels,
    datasets: data.map((chart) => ({
      label: chart.label,
      data: chart.data,
      backgroundColor: chart.backgroundColor,
      hoverBackgroundColor: chart.hoverBackgroundColor,
    })),
  };

  return (
    <div className="StackedBar">
      <Bar options={options} data={chartData} />
    </div>
  );
};
